// apps/admin/src/components/Layout.tsx (FINAL, FULL-FEATURED VERSION)

import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminContext';
import { Home, Building2, UserPlus, BarChart2, Shield, Activity, Settings, FileText, Layers, AlertTriangle, CreditCard, FileSpreadsheet, ShieldCheck, Users, Linkedin, ClipboardCheck, GitBranch, Plug, BadgeCheck, Lightbulb } from 'lucide-react';
import { CollapsibleNav } from './CollapsibleNav';

const themes = {
  dashboard:    { name: 'Dashboard', from: '#6366f1', to: '#8b5cf6', hover: 'rgba(99, 102, 241, 0.15)', active: 'rgba(139, 92, 246, 0.3)', glow: 'shadow-indigo-500/30', icon: Home },
  companies:    { name: 'Companies', from: '#2563eb', to: '#3b82f6', hover: 'rgba(59, 130, 246, 0.15)', active: 'rgba(59, 130, 246, 0.3)', glow: 'shadow-blue-500/30', icon: Building2 },
  onboard:      { name: 'Onboard', from: '#16a34a', to: '#22c55e', hover: 'rgba(34, 197, 94, 0.15)', active: 'rgba(34, 197, 94, 0.3)', glow: 'shadow-green-500/30', icon: UserPlus },
  billing:      { name: 'Billing', from: '#10b981', to: '#059669', hover: 'rgba(16, 185, 129, 0.15)', active: 'rgba(5, 150, 105, 0.3)', glow: 'shadow-emerald-500/30', icon: CreditCard },
  analytics:    { name: 'Analytics', from: '#0891b2', to: '#06b6d4', hover: 'rgba(6, 182, 212, 0.15)', active: 'rgba(6, 182, 212, 0.3)', glow: 'shadow-cyan-500/30', icon: BarChart2 },
  security:     { name: 'Admin Audit', from: '#db2777', to: '#f472b6', hover: 'rgba(244, 114, 182, 0.15)', active: 'rgba(244, 114, 182, 0.3)', glow: 'shadow-pink-500/30', icon: Shield },
  alerts:       { name: 'Security Alerts', from: '#dc2626', to: '#f97316', hover: 'rgba(220, 38, 38, 0.15)', active: 'rgba(249, 115, 22, 0.3)', glow: 'shadow-red-500/30', icon: AlertTriangle },
  reports:      { name: 'Compliance', from: '#059669', to: '#10b981', hover: 'rgba(5, 150, 105, 0.15)', active: 'rgba(16, 185, 129, 0.3)', glow: 'shadow-emerald-500/30', icon: FileText },
  monitoring:   { name: 'Monitoring', from: '#0891b2', to: '#06b6d4', hover: 'rgba(6, 182, 212, 0.15)', active: 'rgba(6, 182, 212, 0.3)', glow: 'shadow-cyan-500/30', icon: Activity },
  featureflags: { name: 'Feature Flags', from: '#a855f7', to: '#ec4899', hover: 'rgba(168, 85, 247, 0.15)', active: 'rgba(236, 72, 153, 0.3)', glow: 'shadow-purple-500/30', icon: Settings },
  aggregation:  { name: 'Aggregation', from: '#f59e0b', to: '#fbbf24', hover: 'rgba(245, 158, 11, 0.15)', active: 'rgba(251, 191, 36, 0.3)', glow: 'shadow-amber-500/30', icon: Layers },
  templates:    { name: 'Templates', from: '#10b981', to: '#34d399', hover: 'rgba(16, 185, 129, 0.15)', active: 'rgba(52, 211, 153, 0.3)', glow: 'shadow-emerald-500/30', icon: FileSpreadsheet },
  dataintegrity: { name: 'Data Integrity', from: '#7c3aed', to: '#a78bfa', hover: 'rgba(124, 58, 237, 0.15)', active: 'rgba(167, 139, 250, 0.3)', glow: 'shadow-violet-500/30', icon: ShieldCheck },
  leads:         { name: 'Marketing Leads', from: '#f59e0b', to: '#f97316', hover: 'rgba(245, 158, 11, 0.15)', active: 'rgba(249, 115, 22, 0.3)', glow: 'shadow-amber-500/30', icon: Users },
  outreach:      { name: 'LinkedIn Outreach', from: '#0077b5', to: '#0a66c2', hover: 'rgba(0, 119, 181, 0.15)', active: 'rgba(10, 102, 194, 0.3)', glow: 'shadow-sky-500/30', icon: Linkedin },
  approvals:     { name: 'Onboarding Approvals', from: '#16a34a', to: '#4ade80', hover: 'rgba(22, 163, 74, 0.15)', active: 'rgba(74, 222, 128, 0.3)', glow: 'shadow-green-500/30', icon: ClipboardCheck },
  loanlifecycle: { name: 'Loan Lifecycle', from: '#0d9488', to: '#14b8a6', hover: 'rgba(13, 148, 136, 0.15)', active: 'rgba(20, 184, 166, 0.3)', glow: 'shadow-teal-500/30', icon: GitBranch },
  crmprovisioning: { name: 'CRM Provisioning', from: '#0d9488', to: '#22d3ee', hover: 'rgba(13, 148, 136, 0.15)', active: 'rgba(34, 211, 238, 0.3)', glow: 'shadow-cyan-500/30', icon: Plug },
  abnreviews: { name: 'ABN Reviews', from: '#0f766e', to: '#14b8a6', hover: 'rgba(20, 184, 166, 0.15)', active: 'rgba(20, 184, 166, 0.3)', glow: 'shadow-teal-500/30', icon: BadgeCheck },
  growth: { name: 'Growth Console', from: '#d97706', to: '#fbbf24', hover: 'rgba(245, 158, 11, 0.15)', active: 'rgba(251, 191, 36, 0.3)', glow: 'shadow-amber-500/30', icon: Lightbulb },
};

type ThemeName = 'dashboard' | 'companies' | 'onboard' | 'billing' | 'analytics' | 'security' | 'alerts' | 'reports' | 'monitoring' | 'featureflags' | 'aggregation' | 'templates' | 'dataintegrity' | 'leads' | 'outreach' | 'approvals' | 'loanlifecycle' | 'crmprovisioning' | 'abnreviews' | 'growth';

const navigation = [
  { href: '/companies', theme: 'companies' as ThemeName },
  { href: '/leads', theme: 'leads' as ThemeName },
  { href: '/outreach', theme: 'outreach' as ThemeName },
  { href: '/companies/onboard', theme: 'onboard' as ThemeName },
  { href: '/onboarding-approvals', theme: 'approvals' as ThemeName },
  { href: '/abn-reviews', theme: 'abnreviews' as ThemeName },
  { href: '/growth', theme: 'growth' as ThemeName },
  { href: '/templates', theme: 'templates' as ThemeName },
  { href: '/billing', theme: 'billing' as ThemeName },
  { href: '/data-integrity', theme: 'dataintegrity' as ThemeName },
  { href: '/loan-lifecycle', theme: 'loanlifecycle' as ThemeName },
  { href: '/crm-provisioning', theme: 'crmprovisioning' as ThemeName },
  { href: '/monitoring', theme: 'monitoring' as ThemeName },
  { href: '/feature-flags', theme: 'featureflags' as ThemeName },
  { href: '/admin-audit', theme: 'security' as ThemeName },
  { href: '/security-alerts', theme: 'alerts' as ThemeName },
  { href: '/compliance-reports', theme: 'reports' as ThemeName },
  { href: '/analytics', theme: 'analytics' as ThemeName },
];

export const Layout: React.FC = () => {
  const { user, signOut: contextSignOut } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTheme, setActiveTheme] = useState<ThemeName>('companies');

  useEffect(() => {
    const currentNav = navigation.find(item => location.pathname.startsWith(item.href));
    const newThemeName = currentNav?.theme || 'companies';
    if (newThemeName !== activeTheme) {
      setActiveTheme(newThemeName);
    }
  }, [location.pathname, activeTheme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', activeTheme);
  }, [activeTheme]);

  const handleSignOut = async () => {
    await contextSignOut();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex bg-slate-900 text-slate-300">
      <CollapsibleNav
        navigation={navigation}
        themes={themes}
        user={user}
        onSignOut={handleSignOut}
      />
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet context={{ activeTheme, themes }} />
        </div>
      </main>
    </div>
  );
};
